import { apiAuth } from "@/api/auth/auth.api";
import { apiEmployees } from "@/api/employees/employee.api";
import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { OrganizationSelectForm } from "@/pages/security/components/OrganizationSelectForm.tsx";
import { TwoFactorVerifyForm } from "@/pages/security/components/TwoFactorVerifyForm.tsx";
import { useEmployeeMeInvalidate } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { ApiResponse } from "@/types/api.types.ts";
import { AppDistribution } from "@/types/common.types.ts";
import {
  ApiAuthorization,
  ApiAuthorizationNextStep,
  ApiAuthorizationTokenType,
} from "@/types/employees/employee.types.ts";
import { ApiIntegrationLoginUrl } from "@/types/integrations/integration.types.ts";
import { ApiEmployeeLoginMethods, ApiLoginMethodOidc } from "@/types/security.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  LinkTo,
  ListItem,
  Stack,
  Text,
  Title,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { z } from "zod";

type OAuthProviderId = "google" | "entra" | "oidc";

interface OAuthProvider {
  id: OAuthProviderId;
  labelKey: string;
  icon: string;
  source: string;
  callbackPath: string;
  getLoginUrl: () => Promise<ApiResponse<ApiIntegrationLoginUrl>>;
  callback: (code: string, state?: string) => Promise<ApiResponse<ApiAuthorization>>;
  isAvailable: boolean;
}

const OAUTH_PROVIDERS: OAuthProvider[] = [
  {
    id: "google",
    labelKey: "common:log_in_with_google",
    icon: "fa-brands fa-google",
    source: "Google OAuth",
    callbackPath: "/login/callback/google",
    getLoginUrl: apiAuth.getGoogleLoginUrl,
    callback: apiAuth.googleCallback,
    isAvailable: true,
  },
  {
    id: "entra",
    labelKey: "common:log_in_with_microsoft",
    icon: "fa-brands fa-microsoft",
    source: "Microsoft Entra ID",
    callbackPath: "/login/callback/entra",
    getLoginUrl: apiAuth.getEntraLoginUrl,
    callback: apiAuth.entraCallback,
    isAvailable: import.meta.env.VITE_APP_DISTRIBUTION !== AppDistribution.headly,
  },
];

const availableOAuthProviders = OAUTH_PROVIDERS.filter((provider) => provider.isAvailable);

const OIDC_CALLBACK_PATH = "/login/callback/oidc";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken } = useAuthStore();
  const { handleErrors } = useErrorHandle();
  const { showError } = useToasts();

  useTitle(t("core:login_to_platform"));

  const emailSchema = z.object({
    email: z
      .string({ error: t("errors:field_required") })
      .min(1, t("errors:field_required"))
      .regex(z.regexes.html5Email, t("errors:field_email_required")),
  });

  const passwordSchema = z.object({
    password: z
      .string({ error: t("errors:password_required") })
      .min(1, t("errors:password_required")),
  });

  type EmailSchema = z.infer<typeof emailSchema>;
  type PasswordSchema = z.infer<typeof passwordSchema>;

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    control: emailControl,
    getValues: getEmailValues,
    formState: { errors: emailErrors },
  } = useForm<EmailSchema>({
    mode: "onSubmit",
    resolver: zodResolver(emailSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    control: passwordControl,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordSchema>({
    mode: "onSubmit",
    resolver: zodResolver(passwordSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingMethods, setIsCheckingMethods] = useState(false);
  const [loginMethods, setLoginMethods] = useState<ApiEmployeeLoginMethods>();
  const [loadingProvider, setLoadingProvider] = useState<OAuthProviderId | null>(null);
  const employeeMeInvalidate = useEmployeeMeInvalidate();
  const [searchParams] = useSearchParams();
  const [tfaState, setTfaState] = useState<{
    step: "login" | "tfa_code" | "tfa_setup";
    accessToken?: string;
  }>({ step: "login" });
  const [orgSelect, setOrgSelect] = useState<{ token: string; source: string } | null>(null);

  const handleLoginSuccess = useCallback(
    (authData: ApiAuthorization, source: string) => {
      if (authData.token_type === ApiAuthorizationTokenType.organization_pending) {
        setOrgSelect({ token: authData.access_token, source });
        return;
      }

      if (authData.token_type === ApiAuthorizationTokenType.tfa_pending) {
        setTfaState({ step: "tfa_code", accessToken: authData.access_token });
        return;
      }

      if (authData.next_step === ApiAuthorizationNextStep.tfa_setup) {
        navigate(`/tfa-setup/${authData.access_token}`);
        return;
      }

      setToken(authData.access_token);
      sendTokenToExtension(authData.access_token);
      analyticsService.trackEvent(analyticEvents.loggedIn, {
        [analyticProperties.source]: source,
      });
      employeeMeInvalidate();

      const nextUrlParam = searchParams.get("next-url");

      // RequireAuth кладёт в state.from location с query — без него теряются consent-параметры /oauth/authorize.
      const from = (
        location.state as { from?: { pathname: string; search?: string; hash?: string } } | null
      )?.from;
      const fromPath = from ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}` : null;

      if (nextUrlParam) {
        const decodedUrl = atob(nextUrlParam);
        if (decodedUrl.startsWith("http://") || decodedUrl.startsWith("https://")) {
          window.location.href = decodedUrl;
        } else {
          navigate(decodedUrl);
        }
      } else if (fromPath) {
        navigate(fromPath);
      } else {
        navigate("/");
      }
    },
    [searchParams, location.state, setToken, employeeMeInvalidate, navigate]
  );

  const handleOAuthLogin = useCallback(async (provider: OAuthProvider) => {
    setLoadingProvider(provider.id);
    const { data, error } = await provider.getLoginUrl();

    if (data) {
      window.location.href = data.authorization_url;
    } else {
      setLoadingProvider(null);
      handleErrors(error);
    }
  }, []);

  const handleOidcLogin = async (option: ApiLoginMethodOidc) => {
    setLoadingProvider("oidc");
    const { data, error } = await apiAuth.getOidcLoginUrl(
      option.organization_id,
      getEmailValues("email")
    );

    if (data) {
      window.location.assign(data.authorization_url);
    } else {
      setLoadingProvider(null);
      handleErrors(error);
    }
  };

  const checkLoginMethods = handleEmailSubmit(async ({ email }) => {
    setIsCheckingMethods(true);
    const { data, error } = await apiEmployees.getLoginMethods(email);
    setIsCheckingMethods(false);

    if (data) {
      setLoginMethods(data);
    } else {
      handleErrors(error, emailControl);
    }
  });

  const onSubmit = handlePasswordSubmit(async ({ password }) => {
    setIsLoading(true);
    const { data, error } = await apiEmployees.login(getEmailValues("email"), password);
    setIsLoading(false);

    if (data) {
      handleLoginSuccess(data, "Web");
    } else {
      handleErrors(error, passwordControl);
    }
  });

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    if (error) {
      showError({
        title: t(`api_errors:${error}`),
      });
    } else if (code) {
      const provider =
        location.pathname === OIDC_CALLBACK_PATH
          ? { id: "oidc" as const, source: "OIDC", callback: apiAuth.oidcCallback }
          : availableOAuthProviders.find((p) => p.callbackPath === location.pathname);

      if (provider) {
        const handleOAuthCallback = async () => {
          setLoadingProvider(provider.id);
          const { data, error } = await provider.callback(code, state || undefined);
          setLoadingProvider(null);

          if (data) {
            handleLoginSuccess(data, provider.source);
          } else {
            handleErrors(error);
          }
        };

        void handleOAuthCallback();
      }
    }
  }, [searchParams, location.pathname]);

  const handleTfaVerifySuccess = useCallback(
    (newAccessToken: string) => {
      handleLoginSuccess(
        {
          access_token: newAccessToken,
          token_type: ApiAuthorizationTokenType.bearer,
          next_step: ApiAuthorizationNextStep.home,
        },
        "Web"
      );
    },
    [handleLoginSuccess]
  );

  return (
    <>
      <FullScreenCard>
        <Stack gap={"sm"} className={"w-full sm:max-w-md "}>
          {tfaState.step === "tfa_code" && tfaState.accessToken && (
            <TwoFactorVerifyForm
              accessToken={tfaState.accessToken}
              onSuccess={handleTfaVerifySuccess}
            />
          )}

          {tfaState.step === "login" && orgSelect && (
            <OrganizationSelectForm
              pendingToken={orgSelect.token}
              onSuccess={(authData) => handleLoginSuccess(authData, orgSelect.source)}
              onExpired={() => setOrgSelect(null)}
            />
          )}

          {tfaState.step === "login" && !orgSelect && (
            <>
              <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
                <Stack gap={"2xl"}>
                  <a
                    href={import.meta.env.VITE_LANDING_URL + "/"}
                    className="flex items-center mb-6 text-2xl font-semibold gap-x-[6px]"
                  >
                    <img src="/logo.png" alt="Skailer" className="h-[28px] w-auto" />
                    <Text className={"text-[22px] leading-[24px]"}>{t("core:app_name")}</Text>
                  </a>
                  <Stack gap={"lg"}>
                    <Title
                      header={t("core:login_to_platform")}
                      size={"lg"}
                      caption={`${t("core:login_to_platform_subtitle")} 🌼`}
                    />

                    <Stack gap="sm">
                      {availableOAuthProviders.map((provider) => (
                        <Button
                          key={provider.id}
                          type={"button"}
                          size="lg"
                          onClick={() => handleOAuthLogin(provider)}
                          className="w-full"
                          isLoading={loadingProvider === provider.id}
                          disabled={loadingProvider !== null || isLoading}
                          leftIcon={<FontAwesomeIcon icon={provider.icon} />}
                        >
                          {t(provider.labelKey)}
                        </Button>
                      ))}
                      {loginMethods?.oidc.map((option) => (
                        <Button
                          key={option.organization_id}
                          type={"button"}
                          size="lg"
                          onClick={() => handleOidcLogin(option)}
                          className="w-full"
                          isLoading={loadingProvider === "oidc"}
                          disabled={loadingProvider !== null || isLoading}
                          leftIcon={<FontAwesomeIcon icon={"fa-light fa-key"} />}
                        >
                          {option.title || t("common:log_in_with_sso")}
                        </Button>
                      ))}
                    </Stack>

                    <Stack>
                      <Box className="flex items-center gap-3">
                        <Box className="h-px flex-1 bg-dark/10" />
                        <Text
                          className={
                            "text-xs font-medium uppercase text-default font-sub-headline tracking-[0.05em] text-secondary"
                          }
                        >
                          {t("common:or")}
                        </Text>
                        <Box className="h-px flex-1 bg-dark/10" />
                      </Box>

                      <form onSubmit={loginMethods ? onSubmit : checkLoginMethods}>
                        <Stack gap={"lg"}>
                          <Box>
                            <Input
                              label={t("core:email")}
                              type="email"
                              {...registerEmail("email", {
                                onChange: () => {
                                  setLoginMethods(undefined);
                                  resetPassword();
                                },
                              })}
                              error={emailErrors.email?.message}
                              disabled={isLoading || loadingProvider !== null}
                            />

                            {loginMethods?.password && (
                              <Input
                                label={t("common:password")}
                                type="password"
                                {...registerPassword("password")}
                                placeholder="••••••••"
                                error={passwordErrors.password?.message}
                              />
                            )}
                          </Box>

                          {!loginMethods ? (
                            <>
                              <ServerErrorField errors={emailErrors} />
                              <Button
                                variant={"secondary"}
                                type={"submit"}
                                size="lg"
                                className="w-full"
                                isLoading={isCheckingMethods}
                                disabled={isCheckingMethods}
                              >
                                {t("common:continue")}
                              </Button>
                            </>
                          ) : (
                            <>
                              {!loginMethods.password && loginMethods.oidc.length === 0 && (
                                <Text className={"text-danger"}>
                                  {t("core:password_login_unavailable")}
                                </Text>
                              )}
                              <ServerErrorField errors={passwordErrors} />
                              {loginMethods.password && (
                                <Button
                                  variant={"secondary"}
                                  type={"submit"}
                                  size="lg"
                                  className="w-full"
                                  isLoading={isLoading}
                                  disabled={isLoading || loadingProvider !== null}
                                >
                                  {t("common:log_in")}
                                </Button>
                              )}
                            </>
                          )}
                        </Stack>
                      </form>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
              <Stack className={"px-6 lg:px-8"}>
                <ListItem
                  title={t("core:forgot_password")}
                  valueSlot={<LinkTo to={"/forgot-password"}>{t("core:restore_password")}</LinkTo>}
                />
              </Stack>
            </>
          )}
        </Stack>
      </FullScreenCard>
    </>
  );
}
